import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import SfhSecondaryButton from './SfhSecondaryButton';
import ProductImage from '../../LegacyOrder/ProductImage';
import SellFromHomeWhatHappensNextDrawer from './SellFromHomeWhatHappensNextDrawer';
import { ReactComponent as InfoIconSecondary } from '../../../assets/images/svg/sfh-info-secondary.svg';
import { ReactComponent as InfoIconBlue } from '../../../assets/images/svg/sfh-info-blue.svg';
import { ReactComponent as HandCoins } from '../../../assets/images/svg/hand-coins.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const SUBTITLE_AND_CAPTION_COLOR = themeColors.colorTextDark;
const PAYOUT_PILL_BG = 'rgba(0, 178, 153, 0.05)';
const VIEW_ORDER_COLOR = '#1C4792';

const ContentWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  z-index: 1;

  > svg {
    width: 20px;
    height: 20px;
    color: ${themeColors.colorTextPrimary};
  }
`;

const CenterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const CenterGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto 0;
`;

const ImageWrap = styled.div`
  > img {
    width: 200px;
    height: 200px;
    object-fit: contain;
    margin: 0;
  }
`;

const Title = styled.h2`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
  margin: 8px 0 0;
  text-align: center;
`;

const Subtitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${SUBTITLE_AND_CAPTION_COLOR};
  text-align: center;
`;

const ProductCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${SUBTITLE_AND_CAPTION_COLOR};
  text-align: center;
  margin-top: 2px;
`;

const PriceGuaranteed = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  margin-bottom: 12px;
`;

const InfoPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid ${themeColors.colorCardBorder};
  border-radius: 6px;
  padding: 8px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const PayoutPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: ${PAYOUT_PILL_BG};
  border: 1px solid ${themeColors.colorBadgeSuccess};
  border-radius: 6px;
  padding: 8px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorPayoutSuccess};

  > svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  > b {
    font-weight: 700;
  }
`;

const WhatHappensNextLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  > svg {
    width: 16px;
    height: 16px;
  }
`;

const Footer = styled.div`
  padding-top: 12px;

  > button {
    width: 100%;
    color: ${VIEW_ORDER_COLOR};
    border-color: ${VIEW_ORDER_COLOR};
  }
`;

const SellFromHomeConfirmation = ({
  selectedHit,
  quantity,
  lockedUnitPrice,
  lockedPayout,
  handleClose,
  handleViewOrder,
}) => {
  const { t } = useTranslation();
  const [isWhatHappensOpen, setIsWhatHappensOpen] = useState(false);

  const productCaption = `${selectedHit?.WeightCaption ?? ''} ${
    selectedHit?.Caption ?? ''
  }`.trim();

  const onViewOrderClick = () => {
    if (typeof handleViewOrder === 'function') handleViewOrder();
  };

  return (
    <ContentWrap>
      <CloseButton
        type="button"
        onClick={handleClose}
        aria-label={t('common.close')}
        data-cy="button-sfh-confirmation-close"
      >
        <Close />
      </CloseButton>
      <ModalBody>
        <CenterGroup>
          <CenterColumn>
            <ImageWrap>
              <ProductImage
                productCode={selectedHit?.Code}
                width={200}
                height={200}
              />
            </ImageWrap>
            <Title>{t('sellWizard.sfhWizard.confirmation.title')}</Title>
            <Subtitle>
              {t('sellWizard.sfhWizard.confirmation.subtitle')}
            </Subtitle>
            <ProductCaption>
              {t('sellWizard.sfhWizard.review.productLine', {
                count: quantity,
                caption: productCaption,
              })}
            </ProductCaption>
            <PriceGuaranteed>
              {t('sellWizard.sfhWizard.confirmation.priceGuaranteed', {
                price: formatMoneyNumeral(lockedUnitPrice),
              })}
            </PriceGuaranteed>
          </CenterColumn>

          <InfoPill>
            <InfoIconSecondary />
            {t('sellWizard.sfhWizard.confirmation.dropOff')}
          </InfoPill>

          <div style={{ height: '12px' }} />

          <PayoutPill>
            <HandCoins />
            {t('sellWizard.sfhWizard.confirmation.payoutReceivable')}
            <b>{formatMoneyNumeral(lockedPayout)}</b>
          </PayoutPill>
        </CenterGroup>

        <CenterColumn>
          <WhatHappensNextLink
            type="button"
            onClick={() => setIsWhatHappensOpen(true)}
            data-cy="link-sfh-what-happens-next"
          >
            <InfoIconBlue />
            {t('sellWizard.sfhWizard.confirmation.whatHappensNext')}
          </WhatHappensNextLink>
        </CenterColumn>

        <Footer>
          <SfhSecondaryButton
            onClick={onViewOrderClick}
            data-cy="button-sfh-view-order"
          >
            {t('sellWizard.sfhWizard.confirmation.viewOrder')}
          </SfhSecondaryButton>
        </Footer>
      </ModalBody>

      <SellFromHomeWhatHappensNextDrawer
        isOpen={isWhatHappensOpen}
        onClose={() => setIsWhatHappensOpen(false)}
      />
    </ContentWrap>
  );
};

SellFromHomeConfirmation.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedUnitPrice: PropTypes.number,
  lockedPayout: PropTypes.number,
  handleClose: PropTypes.func,
  handleViewOrder: PropTypes.func,
};

export default SellFromHomeConfirmation;
